'use client'

export type TcfData = {
    eventStatus: string
    listenerId?: number
    tcString?: string
}

export type TcfCallback = (data: TcfData, success: boolean) => void
export type TcfApi = (command: string, version: number, callback: TcfCallback, parameter?: unknown) => void

type TcfTarget = Pick<Window, '__tcfapi'>

export type TcfReadyObserver = {
    ready: Promise<void>
    recheck: () => void
}

const readinessObservers = new WeakMap<object, TcfReadyObserver>()
const READY_EVENT_STATUSES = new Set(['tcloaded', 'useractioncomplete'])

export function resolveTcfReady(api: TcfApi): Promise<void> {
    return createTcfReadyObserver(api).ready
}

function createTcfReadyObserver(api: TcfApi | undefined): TcfReadyObserver {
    if (!api) {
        return {
            ready: new Promise<void>(() => undefined),
            recheck: () => undefined,
        }
    }

    let resolveReady: () => void
    let resolved = false
    const listenerIds = new Set<number>()
    const removedListenerIds = new Set<number>()
    const ready = new Promise<void>((resolve) => {
        resolveReady = resolve
    })

    const removeListener = (listenerId: number) => {
        if (removedListenerIds.has(listenerId)) {
            return
        }
        removedListenerIds.add(listenerId)
        api('removeEventListener', 2, () => undefined, listenerId)
    }

    const resolveIfReady: TcfCallback = (data, success) => {
        if (!success) {
            return
        }

        if (data.listenerId !== undefined) {
            listenerIds.add(data.listenerId)
            if (resolved) {
                removeListener(data.listenerId)
            }
        }

        if (resolved || !READY_EVENT_STATUSES.has(data.eventStatus)) {
            return
        }

        resolved = true
        listenerIds.forEach(removeListener)
        resolveReady()
    }

    const recheck = () => {
        if (!resolved) {
            api('addEventListener', 2, resolveIfReady)
        }
    }

    recheck()

    return { ready, recheck }
}

export function observeTcfReady(target: TcfTarget = window): TcfReadyObserver {
    const cachedObserver = readinessObservers.get(target)
    if (cachedObserver) {
        return cachedObserver
    }

    const observer = createTcfReadyObserver(target.__tcfapi)
    readinessObservers.set(target, observer)
    return observer
}

export function waitForTcfReady(target: TcfTarget = window): Promise<void> {
    return observeTcfReady(target).ready
}
